import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Filter from "../common/FilterForm";
import _ from "lodash";
import {
  Grid,
  LinearProgress,
  Button,
  Typography,
  SvgIcon,
  Tooltip,
} from "@material-ui/core";
import { useStyles } from "./Report.styles";
import { downloadFile, transformSelectValues } from "../utils/helpers.utils";
import { lastMileUserDataTransform } from "../mocks/reports/response.transforms";
import { AppState } from "../config/redux/reducers";
import {
  fetchReportData,
  fetchDropDownFilter,
  resetData,
  downloadReportData
} from "./redux/reportSlice";
import AppConstants from "../constants";
import GridWrapper from "../common/GridWrapper";
import Loader from "../common/Loader";
import { createFilterPayload } from "../mocks/reports/response.transforms";
import { ReactComponent as refreshIconNew } from "./../assets/icons/refresh-icon-new.svg";
import { useHistory } from "react-router-dom";
import moment from "moment";

const Reports = (props: any) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const onLoadRef = useRef(true);
  const componentClasses = useStyles();
  const { reportdata, tableLoading, loading, dropDownFilters, downloadLink } = useSelector(
    (state: AppState) => state.report
  );

  const query = new URLSearchParams(props.location.search);
  const reportId = query.get("reportId");
  const [listToShow, setListToShow] = useState([]);
  const [userSearchValue, setUserSearchValue] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isModuleSelected, setIsModuleSelected] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    ...AppConstants.REPORT_CONSTANTS.TABLE_WRAPPER_DATA.defaultFiltersObj,
  });
  const [downloadRequested, setDownloadRequested] = useState<string | null>(null);

   const handleDownload = (fileId: string) => {
    if(!fileId) return;
    setDownloadRequested(fileId);
    dispatch(downloadReportData(fileId));
   };

  useEffect(() => {
    if (downloadRequested && downloadLink) {
      downloadFile(downloadLink, "file");
      setDownloadRequested(null);
    }
  }, [downloadLink, downloadRequested]);

  const createHeaderData = (header: any) => {
    const newHeader = { ...header };
    if (newHeader.field === "progress") {
      newHeader.renderCell = (params: any) => (
        <Grid container className={componentClasses.progressContainer}>
          <Grid item className={componentClasses.progressBar}>
            <LinearProgress
              classes={{
                root: componentClasses.linearProgressRoot,
                bar: componentClasses.linearProgressBarRoot,
                colorPrimary: componentClasses.linearProgressColorDefault,
                barColorPrimary:
                  componentClasses.linearProgressBarColorInProgress,
              }}
              variant="determinate"
              value={params.rowData.progress}
            />
          </Grid>
          <Grid item className={componentClasses.progressText}>
            <Typography
              variant="body2"
              color="textSecondary"
            >{`${params.rowData.progress}%`}</Typography>
          </Grid>
        </Grid>
      );
    }
    if (header.field === "reportDateRange") {
      newHeader.renderCell = () => {
        return (
          <Typography className={componentClasses.dateRange}>
               {`${moment(new Date()).format(AppConstants.DATE_FORMAT)} - ${moment(new Date()).format(AppConstants.DATE_FORMAT)}`}
             </Typography>
        );
      };
    }
    if (newHeader.field === "actions") {
      newHeader.renderCell = (params: any) => (
        <Grid container className={componentClasses.actionsHeader}>
          <Grid className="actionItem" item>
            <Button
              variant="text"
              color="primary"
              onClick={() => handleDownload(params.rowData.id)}
              disabled={
                params.rowData.progress !== 100 ||
                params.rowData.status !== "COMPLETED"
              }
            >
              Download
            </Button>
          </Grid>
        </Grid>
      );
    }
    return newHeader;
  };

  const [tableWrapperData, setTableWrapperData] = useState({
    ...AppConstants.REPORT_CONSTANTS.TABLE_WRAPPER_DATA,
    HeaderData: AppConstants.REPORT_CONSTANTS.TABLE_WRAPPER_DATA.HeaderData.map(
      createHeaderData
    ),
  });

  const filterCallback = (filters: any, isLoadMore?: any, id?: string) => {
    if (!isFilterApplied && !isLoadMore) return;
    if (filters) {
      const payloadFilters = _.cloneDeep(filters);
      payloadFilters[AppConstants.COUNTRY_CODE] =
        sessionStorage.getItem(AppConstants.COUNTRY_CODE) || "";
      payloadFilters.pageSize = tableWrapperData.loadMore.pageSize;
    
      if (id) {
        payloadFilters.requestId = id;
      }
      const payload = createFilterPayload(
        payloadFilters,
        reportdata,
        isLoadMore
      );
      setIsFilterApplied(false);
      dispatch(fetchReportData(payload));
    }
  };
  const searchFilterHandler = (
    data: { [key: string]: string },
    updatedSearchValue: string,
    filters: any
  ) => {
    onLoadRef.current = false;
    setUserSearchValue(updatedSearchValue);
    if (updatedSearchValue) {
      for (const searchKey of Object.keys(data)) {
        data[searchKey] = _.trim(data[searchKey]);
        if (
          data[searchKey] &&
          data[searchKey].length >= AppConstants.SEARCH_BOX_LIMIT
        ) {
          const searchPayload = _.cloneDeep(filters);
          searchPayload.search = data;
          filterCallback(searchPayload);
          return;
        }
      }
    }
  };

  const handleLoadMoreDisableState = useCallback(
    () => {
        const loadMoreState = tableWrapperData.loadMore;
        loadMoreState.disabled = reportdata.totalPages ? reportdata.pageNumber + 1 >= reportdata.totalPages : true;
        setTableWrapperData({...tableWrapperData, loadMore: loadMoreState});
    },
    [tableWrapperData, reportdata]
);

  useEffect(() => {
    setListToShow(lastMileUserDataTransform(reportdata) as any);
    handleLoadMoreDisableState();
  }, [reportdata]);

  useEffect(() => {
    if (reportId) {
      setIsFilterApplied(true);
    }
  }, [reportId]);

  const fetchCallbackOnOrder = (name: string, value: any) => {
    setActiveFilters((activeFilters: any) => ({
      ...activeFilters,
      [name]: {
        ...activeFilters[name],
        value: value,
      },
    }));
    if (name === "dateRange") {
      setTableWrapperData((prevData) => ({
        ...prevData,
        HeaderData: prevData.HeaderData.map((header: any) => {
          if (header.field === "reportDateRange") {
            const { startDate, endDate } = value;
            return {
              ...header,
              renderCell: () => (
                <Typography className={componentClasses.dateRange}>
                  {`${startDate} - ${endDate}`}
                </Typography>
              ),
            };
          }
          return header;
        }),
      }));
    }
    if (name === "module") {
      setIsModuleSelected(true);
    }
    setIsFilterApplied(true);

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("reportId");
    history.replace({ pathname: newUrl.pathname, search: newUrl.search });
  };

  useEffect(() => {
    if (isFilterApplied) {
      if (reportId) {
        filterCallback(activeFilters, false, reportId);
      }
      else{
        filterCallback(activeFilters);
      }
    }
  }, [activeFilters, isFilterApplied, reportId]);

  const fillFilterDropDownvalue = () => {
    const headerData = tableWrapperData.FilterFormData.map((header: any) => {
      if (header) {
        if (header.fieldName === "module") {
          const module = transformSelectValues(dropDownFilters?.module);
          header.items = module || [];
        }
        if (header.fieldName === "type") {
          const type = transformSelectValues(dropDownFilters?.type);
          header.items = type || [];
        }
      }
      return header;
    });
    setTableWrapperData({ ...tableWrapperData, FilterFormData: headerData });
  };

  useEffect(() => {
    if (reportdata.totalElements > 0) {
      const ordersHeaderData = tableWrapperData.HeaderData.map(
        (header: any) => {
          if (header.filterObj) {
            if (header.filterObj.fieldName === "status") {
              header.filterObj.items =
                transformSelectValues(dropDownFilters?.status) || [];
            }
          }
          return header;
        }
      );
      setTableWrapperData({
        ...tableWrapperData,
        HeaderData: ordersHeaderData,
      });
    }
  }, [dropDownFilters, reportdata]);

  useEffect(() => {
    if (Object.keys(dropDownFilters).length !== 0) {
      fillFilterDropDownvalue();
    }
  }, [dropDownFilters]);

  useEffect(() => {
    dispatch(fetchDropDownFilter());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetData());
    };
  }, [dispatch]);

  const handleRefreshClick = () => {
    setIsFilterApplied(true);
    filterCallback(activeFilters);
  };

  return (
    <div className={componentClasses.root}>
      {loading && <Loader />}
      <Grid item className={componentClasses.filterSection}>
        <Grid container direction="column">
          <Grid className="filtersContent" container>
            <Grid className={componentClasses.filterFormWrapper} container>
              {AppConstants.REPORT_CONSTANTS.TABLE_WRAPPER_DATA.FilterFormData
                .length
                ? AppConstants.REPORT_CONSTANTS.TABLE_WRAPPER_DATA.FilterFormData.map(
                    (filter: any) => (
                      <Filter
                        key={filter.key}
                        filter={filter}
                        defaultValues={tableWrapperData.defaultFiltersObj}
                        initialValues={{
                          name: `Select ${filter.label}`,
                          value: AppConstants.SELECT_NONE.value,
                        }}
                        fetchDataCallback={fetchCallbackOnOrder}
                        disabled={
                          filter.fieldName !== "module" && !isModuleSelected
                        }
                      />
                    )
                  )
                : null}
              {reportdata.totalElements > 0 && (
                <Tooltip title="Refresh Data">
                  <SvgIcon
                    className="icon"
                    onClick={handleRefreshClick}
                    component={refreshIconNew}
                    style={{ marginLeft: "8px", alignSelf: "center" }}
                  />
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid className={componentClasses.container} container>
        <Grid className="userItem content" item>
          <GridWrapper
            showHeader={true}
            checkboxSelection={false}
            headerData={tableWrapperData.HeaderData}
            rowData={listToShow}
            loading={loading ? false : tableLoading}
            defaultFilter={tableWrapperData.defaultFiltersObj}
            activeFilter={activeFilters}
            title={tableWrapperData.title}
            disableSelectionOnClick={true}
            searchValue={userSearchValue}
            loadMore={{
              ...tableWrapperData.loadMore,
              rowCount: reportdata.totalElements || 0,
            }}
            searchCallback={searchFilterHandler}
            filterGrid={filterCallback}
            searchTypes={[]}
            callOnActionOnly={true}
            hideTopHeader={true}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default Reports;
